import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { sendWelcomeEmail, sendAdminNotification } from './lib/email';
import { validateRucFormat } from './lib/validateRuc';
import logoUrl from './assets/storsafe-logo.png';

interface Props {
  onBack:        () => void;
  initialPlan?:  'starter' | 'professional' | 'enterprise';
  initialCycle?: 'monthly' | 'annual';
  /** Flujo B: contratación directa sin trial (viene de /register?plan=&cycle=) */
  skipTrial?:    boolean;
}

type PageState = 'form' | 'check-email';

type RegistroPrecheck =
  | { ok: true; rucRegistrado: boolean }
  | { ok: false; field: 'email' | 'form'; message: string };

// Valida el correo corporativo (lista negra + MX) y consulta si el RUC ya
// está registrado, en una sola llamada con límite de intentos por conexión.
async function precheckRegistro(email: string, ruc: string): Promise<RegistroPrecheck> {
  try {
    const { data, error } = await supabase.functions.invoke('validate-corporate-email', {
      body: { email, ruc },
    });
    if (error) {
      if ((error as any).context?.status === 429) {
        return { ok: false, field: 'form', message: 'Recibimos demasiados intentos de registro desde esta conexión. Espera unos minutos y vuelve a intentarlo.' };
      }
      return { ok: false, field: 'email', message: 'No pudimos validar tu correo. Intenta nuevamente.' };
    }
    if (!data?.valid) {
      return { ok: false, field: 'email', message: data?.error || 'Usa el correo de tu empresa, ej. nombre@tuempresa.com. No aceptamos correos personales o temporales.' };
    }
    return { ok: true, rucRegistrado: data.ruc_registrado === true };
  } catch {
    return { ok: false, field: 'email', message: 'No pudimos validar tu correo. Intenta nuevamente.' };
  }
}

interface PasswordStrength {
  score: number;
  label: 'Débil' | 'Media' | 'Fuerte';
  color: string;
}

function getPasswordStrength(pwd: string): PasswordStrength {
  let score = 0;
  if (pwd.length >= 8)        score++;
  if (/[A-Z]/.test(pwd))      score++;
  if (/[0-9]/.test(pwd))      score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;

  if (score <= 1) return { score, label: 'Débil',  color: '#ef4444' };
  if (score <= 3) return { score, label: 'Media',  color: '#f59e0b' };
  return { score, label: 'Fuerte', color: '#22c55e' };
}

function validatePhone(phone: string): string | null {
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) return 'Ingresa un teléfono ecuatoriano válido (10 dígitos)';
  const prefix2 = digits.slice(0, 2);
  const validPrefixes = ['09', '02', '03', '04', '05', '06', '07'];
  if (!validPrefixes.includes(prefix2)) {
    return 'El teléfono debe empezar en 09 (móvil) o 02–07 (fijo)';
  }
  return null;
}

export default function RegisterPage({ onBack, initialPlan = 'starter', initialCycle = 'monthly', skipTrial = false }: Props) {
  const [pageState, setPageState] = useState<PageState>('form');

  // Persiste plan/cycle en localStorage para que sobrevivan al flujo de
  // confirmación por correo (el link puede abrirse en otra pestaña/sesión,
  // donde el sessionStorage de este tab no está disponible).
  useEffect(() => {
    if (!skipTrial) return;
    localStorage.setItem('ss_direct_hire_plan', initialPlan);
    localStorage.setItem('ss_direct_hire_cycle', initialCycle);
  }, [skipTrial, initialPlan, initialCycle]);

  // Personal fields
  const [fullName, setFullName]   = useState('');
  const [cargo, setCargo]         = useState('');
  const [email, setEmail]         = useState('');
  const [phone, setPhone]         = useState('');
  const [password, setPassword]   = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  // Company fields
  const [companyName, setCompanyName] = useState('');
  const [ruc, setRuc]                 = useState('');
  const [direccion, setDireccion]     = useState('');

  // UI state
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [fieldErrors, setFieldErrors]   = useState<Record<string, string>>({});
  const [registeredEmail, setRegisteredEmail] = useState('');

  const pwdMatch     = password === confirmPwd;
  const pwdStrength  = password ? getPasswordStrength(password) : null;

  function clearError(field: string) {
    setError('');
    setFieldErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};

    if (!fullName.trim()) errors.fullName = 'Requerido';
    if (!cargo.trim())    errors.cargo    = 'Requerido';

    const phoneErr = validatePhone(phone.trim());
    if (phoneErr) errors.phone = phoneErr;

    const rucErr = validateRucFormat(ruc.trim());
    if (rucErr) errors.ruc = rucErr;

    if (!companyName.trim()) errors.companyName = 'Requerido';
    if (password.length < 8)  errors.password   = 'Mínimo 8 caracteres';
    if (!pwdMatch)             errors.confirmPwd = 'Las contraseñas no coinciden';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const canSubmit =
    fullName.trim().length > 0 &&
    cargo.trim().length > 0 &&
    email.trim().length > 0 &&
    phone.trim().length > 0 &&
    ruc.trim().length > 0 &&
    companyName.trim().length > 0 &&
    password.length >= 8 &&
    pwdMatch;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;
    setError('');
    setLoading(true);

    const cleanRuc   = ruc.replace(/\D/g, '');
    const cleanPhone = phone.replace(/\D/g, '');

    // Correo corporativo + RUC duplicado, antes de crear el usuario de Auth
    // (Edge Function con límite de intentos por conexión).
    const pre = await precheckRegistro(email.trim(), cleanRuc);
    if (!pre.ok) {
      if (pre.field === 'email') setFieldErrors((prev) => ({ ...prev, email: pre.message }));
      else setError(pre.message);
      setLoading(false);
      return;
    }
    if (pre.rucRegistrado) {
      setFieldErrors((prev) => ({ ...prev, ruc: 'Este RUC ya está registrado en StorSafe.' }));
      setLoading(false);
      return;
    }

    // Store for email-confirmation fallback in App.tsx onAuthStateChange
    sessionStorage.setItem(
      'pending_registration',
      JSON.stringify({
        companyName: companyName.trim(),
        ruc:         cleanRuc,
        country:     'EC',
        fullName:    fullName.trim(),
        cargo:       cargo.trim(),
        phone:       cleanPhone,
        plan:        initialPlan,
        cycle:       initialCycle,
        skipTrial,
      })
    );

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { data: { full_name: fullName.trim() } },
    });

    if (signUpError) {
      sessionStorage.removeItem('pending_registration');
      setError(translateError(signUpError.message));
      setLoading(false);
      return;
    }

    if (data.session) {
      const { error: regError } = await supabase.rpc('register_company', {
        p_company_name:  companyName.trim(),
        p_ruc:           cleanRuc,
        p_country:       'EC',
        p_full_name:     fullName.trim(),
        p_cargo:         cargo.trim(),
        p_phone:         cleanPhone,
        p_plan:          initialPlan,
        p_billing_cycle: skipTrial ? initialCycle : null,
        p_skip_trial:    skipTrial,
        p_direccion:     direccion.trim(),
      });

      sessionStorage.removeItem('pending_registration');

      if (regError) {
        if (regError.message.includes('ruc_already_exists')) {
          setFieldErrors((prev) => ({ ...prev, ruc: 'Este RUC ya está registrado en StorSafe.' }));
        } else {
          setError('Error al crear la empresa. Por favor intenta nuevamente.');
        }
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      // Fire-and-forget — email failures never block the user
      // El servidor toma destinatario y datos de la base (A5)
      sendWelcomeEmail();
      sendAdminNotification();

      // Si el visitante eligió un plan/ciclo desde /pricing antes de registrarse,
      // lo llevamos directo a ese plan en vez del dashboard.
      const pendingPlan = sessionStorage.getItem('ss_pending_plan');
      if (pendingPlan) {
        const pendingCycle = sessionStorage.getItem('ss_pending_cycle') ?? 'monthly';
        sessionStorage.removeItem('ss_pending_plan');
        sessionStorage.removeItem('ss_pending_cycle');
        window.location.href = `/pricing?plan=${pendingPlan}&cycle=${pendingCycle}`;
        return;
      }
    } else {
      setRegisteredEmail(email.trim());
      setPageState('check-email');
    }

    setLoading(false);
  }

  // ── Check email screen ───────────────────────────────────────────────────────
  if (pageState === 'check-email') {
    return (
      <div className="login-root">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div className="login-logo">
            <img src={logoUrl} alt="StorSafe" className="login-logo-img" />
          </div>
          <div style={{
            marginTop: 28,
            padding: '24px 20px',
            background: 'rgba(47,191,113,0.07)',
            borderRadius: 12,
            border: '1px solid rgba(47,191,113,0.2)',
          }}>
            <p style={{ fontSize: '2.2rem', lineHeight: 1 }}>📧</p>
            <p style={{ color: '#e2e8f0', fontWeight: 600, marginTop: 14, fontSize: '1rem' }}>
              Revisa tu correo
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.84rem', marginTop: 10, lineHeight: 1.65 }}>
              Enviamos un enlace de confirmación a{' '}
              <strong style={{ color: '#e2e8f0' }}>{registeredEmail}</strong>.
              Haz clic en él para activar tu cuenta y acceder al dashboard.
            </p>
          </div>
          <button
            onClick={onBack}
            style={{ marginTop: 20, background: 'none', border: 'none', color: '#2FBF71', cursor: 'pointer', fontSize: '0.85rem' }}
          >
            ← Volver al inicio de sesión
          </button>
          <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  // ── Registration form ────────────────────────────────────────────────────────
  return (
    <div className="login-root">
      <div className="login-card" style={{ maxWidth: 480 }}>
        <div className="login-logo">
          <img src={logoUrl} alt="StorSafe" className="login-logo-img" />
        </div>

        <p className="login-tagline">{skipTrial ? 'Crea tu cuenta para continuar tu compra' : 'Crea tu cuenta · 5 días gratis'}</p>

        <form className="login-form" onSubmit={handleSubmit}>

          {/* ── Sección personal ── */}
          <div className="reg-section-label">Datos del solicitante</div>

          <FieldGroup label="Nombre completo" error={fieldErrors.fullName} required>
            <UserIcon />
            <input
              type="text"
              placeholder="Juan Pérez"
              value={fullName}
              onChange={(e) => { setFullName(e.target.value); clearError('fullName'); }}
              autoFocus
              disabled={loading}
            />
          </FieldGroup>

          <FieldGroup label="Cargo" error={fieldErrors.cargo} required>
            <BriefcaseIcon />
            <input
              type="text"
              placeholder="Gerente de Operaciones"
              value={cargo}
              onChange={(e) => { setCargo(e.target.value); clearError('cargo'); }}
              disabled={loading}
            />
          </FieldGroup>

          <FieldGroup label="Correo corporativo" error={fieldErrors.email} hideErrorText required>
            <EmailIcon />
            <input
              type="email"
              placeholder="juan@empresa.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearError('email'); }}
              autoComplete="email"
              disabled={loading}
            />
          </FieldGroup>

          {fieldErrors.email && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              marginTop: -8, padding: '12px 14px',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: 8,
            }}>
              <AlertTriangleIcon />
              <p style={{ color: '#fca5a5', fontSize: '0.86rem', fontWeight: 500, lineHeight: 1.5, margin: 0 }}>
                {fieldErrors.email}
              </p>
            </div>
          )}

          <FieldGroup label="Teléfono de contacto" error={fieldErrors.phone} required>
            <PhoneIcon />
            <input
              type="tel"
              placeholder="0987654321"
              value={phone}
              onChange={(e) => { setPhone(e.target.value.replace(/[^\d+\-() ]/g, '')); clearError('phone'); }}
              disabled={loading}
            />
          </FieldGroup>

          <FieldGroup label="Contraseña" error={fieldErrors.password} required>
            <LockIcon />
            <input
              type="password"
              placeholder="Mínimo 8 caracteres"
              value={password}
              onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
              minLength={8}
              disabled={loading}
            />
          </FieldGroup>

          {pwdStrength && (
            <div style={{ marginTop: -8 }}>
              <div style={{ height: 4, borderRadius: 2, background: '#1e293b', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${(pwdStrength.score / 4) * 100}%`,
                  background: pwdStrength.color,
                  transition: 'width 0.2s, background 0.2s',
                }} />
              </div>
              <p style={{ fontSize: '0.74rem', color: pwdStrength.color, fontWeight: 600, marginTop: 4, marginBottom: 0 }}>
                Fortaleza: {pwdStrength.label}
              </p>
            </div>
          )}

          <FieldGroup
            label="Confirmar contraseña"
            error={fieldErrors.confirmPwd ?? (confirmPwd && !pwdMatch ? 'Las contraseñas no coinciden' : '')}
            required
          >
            <LockIcon />
            <input
              type="password"
              placeholder="Repite tu contraseña"
              value={confirmPwd}
              onChange={(e) => { setConfirmPwd(e.target.value); clearError('confirmPwd'); }}
              disabled={loading}
            />
          </FieldGroup>

          {/* ── Sección empresa ── */}
          <div className="reg-section-label" style={{ marginTop: 4 }}>Datos de la empresa</div>

          <FieldGroup label="Nombre de empresa" error={fieldErrors.companyName} required>
            <BuildingIcon />
            <input
              type="text"
              placeholder="Mi Empresa S.A."
              value={companyName}
              onChange={(e) => { setCompanyName(e.target.value); clearError('companyName'); }}
              disabled={loading}
            />
          </FieldGroup>

          <FieldGroup label="RUC empresarial" error={fieldErrors.ruc} required hint="13 dígitos, terminado en 001">
            <HashIcon />
            <input
              type="text"
              placeholder="1790012345001"
              value={ruc}
              onChange={(e) => { setRuc(e.target.value.replace(/\D/g, '').slice(0, 13)); clearError('ruc'); }}
              maxLength={13}
              disabled={loading}
            />
          </FieldGroup>

          <FieldGroup label="Dirección de la empresa" error={fieldErrors.direccion}>
            <BuildingIcon />
            <input
              type="text"
              placeholder="Ej: Av. Amazonas N12-34, Quito"
              value={direccion}
              onChange={(e) => { setDireccion(e.target.value); clearError('direccion'); }}
              maxLength={200}
              disabled={loading}
            />
          </FieldGroup>

          {error && <p className="login-error">{error}</p>}

          <button
            type="submit"
            className="btn-login"
            disabled={loading || !canSubmit}
          >
            {loading ? <span className="btn-spinner" /> : (skipTrial ? 'Continuar' : 'Crear cuenta gratis')}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.84rem', color: '#64748b' }}>
          ¿Ya tienes cuenta?{' '}
          <button
            onClick={onBack}
            style={{ background: 'none', border: 'none', color: '#2FBF71', cursor: 'pointer', fontSize: '0.84rem', fontWeight: 600, padding: 0 }}
          >
            Inicia sesión
          </button>
        </p>

        <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
      </div>

      <style>{`
        .reg-section-label {
          font-size: 0.72rem;
          font-weight: 700;
          color: #2FBF71;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          border-bottom: 1px solid #1e3a2f;
          padding-bottom: 6px;
          margin-bottom: -4px;
        }
        .field-error {
          font-size: 0.75rem;
          color: #f87171;
          margin-top: 3px;
          padding-left: 2px;
        }
        .field-hint {
          font-size: 0.72rem;
          color: #475569;
          margin-top: 3px;
          padding-left: 2px;
        }
      `}</style>
    </div>
  );
}

// ── Reusable field wrapper ───────────────────────────────────────────────────

interface FieldGroupProps {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  hideErrorText?: boolean;
  children: React.ReactNode;
}

function FieldGroup({ label, required, error, hint, hideErrorText, children }: FieldGroupProps) {
  return (
    <div className="field-group">
      <label>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      <div className="input-wrapper" style={error ? { borderColor: '#ef4444' } as any : undefined}>
        {children}
      </div>
      {error && !hideErrorText && <p className="field-error">{error}</p>}
      {hint && !error && <p className="field-hint">{hint}</p>}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function translateError(msg: string): string {
  if (msg.includes('already registered') || msg.includes('User already registered'))
    return 'Este correo ya tiene una cuenta. Intenta iniciar sesión.';
  if (msg.includes('Password should be'))
    return 'La contraseña debe tener al menos 8 caracteres.';
  if (msg.includes('Network'))
    return 'Sin conexión. Verifica tu internet.';
  return 'Error al crear la cuenta. Intenta nuevamente.';
}

// ── SVG Icons ────────────────────────────────────────────────────────────────

function UserIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
      <line x1="12" y1="12" x2="12" y2="12" />
      <path d="M2 12h20" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8a19.79 19.79 0 01-3.07-8.67A2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14h0v2.92z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
    </svg>
  );
}

function AlertTriangleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function HashIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" />
    </svg>
  );
}
